import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MistLayer } from "@/components/MistLayer";

const HERO_IMG = "/art/hero.webp";

const lines = [
  { text: "Considered websites", italic: "Considered", delay: 0.25 },
  { text: "for small business.", italic: null, delay: 0.42 },
];

const lineAnim = (delay) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 1, delay, ease: [0.22, 1, 0.36, 1] },
});

const Hero = ({ onNavigate }) => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 140]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.4]);

  return (
    <section className="relative isolate flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Ink-wash painting of misty jade mountains and bamboo"
          className="h-[120%] w-full object-cover"
        />
        <MistLayer />
        <div className="absolute inset-0 bg-gradient-to-r from-paper via-paper/60 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-paper to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-paper to-transparent" />
      </motion.div>

      <motion.img
        src="/art/enso.webp"
        alt=""
        aria-hidden="true"
        data-testid="hero-enso"
        initial={{ opacity: 0, scale: 0.9, rotate: -20 }}
        animate={{ opacity: 0.55, scale: 1, rotate: 0 }}
        transition={{ duration: 2, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-[8%] top-[18%] hidden w-[300px] lg:block xl:w-[340px]"
      />

      <motion.div
        style={{ opacity: fade }}
        className="mx-auto w-full max-w-7xl px-6 pb-24 pt-36 lg:px-10"
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8 text-xs font-medium uppercase tracking-[0.3em] text-jade"
          data-testid="hero-eyebrow"
        >
          Vagus Digital · Independent web studio
        </motion.p>

        <h1
          className="max-w-4xl font-display text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          data-testid="hero-heading"
        >
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(line.delay)}>
                {line.italic ? (
                  <>
                    <em className="italic text-jade">{line.italic}</em>
                    {line.text.replace(line.italic, "")}
                  </>
                ) : (
                  line.text
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-ink2 sm:text-lg"
          data-testid="hero-subline"
        >
          I design, build and look after your website personally, for small
          businesses across the UK and beyond. The person you talk to is the
          person writing your code. Web design, AI tools, SEO and social,
          all under one roof.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-primary-cta"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("#contact");
            }}
            className="rounded-full bg-jade px-8 py-3.5 text-sm font-medium text-paper shadow-ambient transition-colors duration-300 hover:bg-ink"
          >
            Start a project
          </a>
          <a
            href="#work"
            data-testid="hero-secondary-cta"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("#work");
            }}
            className="rounded-full border border-jade/30 px-8 py-3.5 text-sm font-medium text-jade transition-colors duration-300 hover:border-jade hover:bg-jade/5"
          >
            See sample work
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-10 right-8 hidden flex-col items-center gap-3 lg:flex"
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.25em] text-jade/70">
          Scroll
        </span>
        <span className="scroll-cue block h-14" />
      </motion.div>
    </section>
  );
};

export default Hero;